import React, { useState } from "react";
import toast from "react-hot-toast";
import {
  useCustomers,
  useCustomerStats,
  useUpdateCustomerContact,
  useChangeCustomerPassword,
  useUpdateCustomerZipCode,
  useUpdateCustomerCountry,
  useUpdateCustomerSponsor,
} from "../hooks/useCustomers";
import { useLocalStorage } from "../hooks/useLocalStorage";
import SearchBar from "../components/common/SearchBar";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Pagination from "../components/common/Pagination";
import ExportCustomersModal from "../components/customers/ExportCustomersModal";
import CustomToast from "../components/common/CustomToast";
import FamilyAccountSection from "../components/family/FamilyAccountSection";
import { Customer, apiService } from "../services/api";
import {
  FunnelIcon,
  UserGroupIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  Squares2X2Icon,
  ListBulletIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  DocumentArrowDownIcon,
  KeyIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";

type ViewMode = "card" | "list";
type FilterType = "all" | "active" | "inactive";

const Customers: React.FC = () => {
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null,
  );
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isLoadingAllCustomers, setIsLoadingAllCustomers] = useState(false);
  // Set when the detail modal was opened from a family badge: the Family
  // Account section scrolls into view.
  const [focusFamilySection, setFocusFamilySection] = useState(false);

  // Inline edit states
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [isEditingZipCode, setIsEditingZipCode] = useState(false);
  const [editZipCode, setEditZipCode] = useState("");
  const [isEditingCountry, setIsEditingCountry] = useState(false);
  const [editCountry, setEditCountry] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPhonePrefix, setEditPhonePrefix] = useState("");
  const [editPhoneNumber, setEditPhoneNumber] = useState("");

  // Password change states
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [isEditingSponsorId, setIsEditingSponsorId] = useState(false);
  const [editSponsorId, setEditSponsorId] = useState("");
  const [isEditingSponsorType, setIsEditingSponsorType] = useState(false);
  const [editSponsorType, setEditSponsorType] = useState<
    "dealer" | "sales_rep" | "office"
  >("office");

  const updateContactMutation = useUpdateCustomerContact();
  const changePasswordMutation = useChangeCustomerPassword();
  const updateZipCodeMutation = useUpdateCustomerZipCode();
  const updateCountryMutation = useUpdateCustomerCountry();
  const updateCustomerSponsorMutation = useUpdateCustomerSponsor();
  const [filter, setFilter] = useLocalStorage<FilterType>(
    "customers-filter",
    "all",
  );
  const [searchQuery, setSearchQuery] = useLocalStorage<string>(
    "customers-search-query",
    "",
  );
  const [viewMode, setViewMode] = useLocalStorage<ViewMode>(
    "customers-view-mode",
    "card",
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(20);

  const { data: customersResponse, isLoading } = useCustomers({
    limit: itemsPerPage,
    skip: (currentPage - 1) * itemsPerPage,
    search: searchQuery || undefined,
    status: filter !== "all" ? filter : undefined,
  });

  const { data: stats } = useCustomerStats();

  const customers = customersResponse?.data || [];
  const totalItems = customersResponse?.total || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchQuery, setCurrentPage]);

  const handleViewDetails = (customer: Customer, focusFamily = false) => {
    setSelectedCustomer(customer);
    setFocusFamilySection(focusFamily);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedCustomer(null);
    setFocusFamilySection(false);
    setIsEditingEmail(false);
    setIsEditingPhone(false);
    setIsEditingZipCode(false);
    setIsChangingPassword(false);
    setNewPassword("");
    setConfirmPassword("");
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  // Email edit handlers
  const handleStartEditEmail = () => {
    setEditEmail(selectedCustomer?.email || "");
    setIsEditingEmail(true);
  };

  const handleCancelEditEmail = () => {
    setIsEditingEmail(false);
    setEditEmail("");
  };

  const handleSaveEmail = async () => {
    if (!selectedCustomer?._id) return;
    try {
      await updateContactMutation.mutateAsync({
        customerId: selectedCustomer._id,
        contactData: { email: editEmail },
      });
      setSelectedCustomer({ ...selectedCustomer, email: editEmail });
      setIsEditingEmail(false);
    } catch (error: any) {
      // 409 EMAIL_IN_USE: the address belongs to another customer, a dealer,
      // an active family member or a pending family invitation.
      const message = error?.response?.data?.message;
      toast.custom(
        <CustomToast
          message={
            typeof message === "string" && message.trim()
              ? message
              : "Failed to update customer e-mail"
          }
          type="error"
        />,
        { duration: 6000 },
      );
    }
  };

  // Phone edit handlers
  const handleStartEditPhone = () => {
    setEditPhonePrefix(selectedCustomer?.phonePrefix || "");
    setEditPhoneNumber(selectedCustomer?.phoneNumber || "");
    setIsEditingPhone(true);
  };

  const handleCancelEditPhone = () => {
    setIsEditingPhone(false);
    setEditPhonePrefix("");
    setEditPhoneNumber("");
  };

  const handleSavePhone = async () => {
    if (!selectedCustomer?._id) return;
    try {
      await updateContactMutation.mutateAsync({
        customerId: selectedCustomer._id,
        contactData: {
          phonePrefix: editPhonePrefix,
          phoneNumber: editPhoneNumber,
        },
      });
      setSelectedCustomer({
        ...selectedCustomer,
        phonePrefix: editPhonePrefix,
        phoneNumber: editPhoneNumber,
      });
      setIsEditingPhone(false);
    } catch (error) {
      console.error("Failed to update customer phone number.");
    }
  };

  // Password change handler
  const handleChangePassword = async () => {
    if (!selectedCustomer?._id) return;

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      await changePasswordMutation.mutateAsync({
        customerId: selectedCustomer._id,
        newPassword,
      });
      toast.success("Customer password updated successfully");
      setIsChangingPassword(false);
      setNewPassword("");
      setConfirmPassword("");
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to update password");
    }
  };

  const getStatusBadge = (status?: string) => {
    const statusLower = status?.toLowerCase() || "active";
    const badges = {
      active:
        "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200",
      inactive: "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300",
    };
    return badges[statusLower as keyof typeof badges] || badges.active;
  };

  const loadAllCustomers = async (): Promise<Customer[]> => {
    setIsLoadingAllCustomers(true);
    try {
      const response = await apiService.getCustomers({
        limit: 10000, // Large limit to get all customers
        skip: 0,
        search: searchQuery || undefined,
        status: filter !== "all" ? filter : undefined,
      });
      return response.data || [];
    } finally {
      setIsLoadingAllCustomers(false);
    }
  };

  // A search by a family member's e-mail returns the family owner with a
  // familyBadge (members are not customers).
  const renderFamilyBadge = (customer: Customer) => {
    if (!customer.familyBadge) return null;
    const { status } = customer.familyBadge;
    const hint = `The searched e-mail belongs to a family member (${status}). This customer is that member's family admin. Click to open the Family Account section.`;
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          handleViewDetails(customer, true);
        }}
        title={hint}
        aria-label={hint}
        className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 hover:bg-purple-200 dark:hover:bg-purple-800 max-w-full truncate"
        data-testid={`family-badge-${customer.customerId}`}
      >
        Family member · {status}
      </button>
    );
  };

  const renderViewModeButtons = () => (
    <div className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
      <button
        onClick={() => setViewMode("card")}
        className={`p-2 rounded-md transition-colors ${
          viewMode === "card"
            ? "bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow-sm"
            : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
        }`}
        title="Card View"
      >
        <Squares2X2Icon className="h-5 w-5" />
      </button>
      <button
        onClick={() => setViewMode("list")}
        className={`p-2 rounded-md transition-colors ${
          viewMode === "list"
            ? "bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow-sm"
            : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
        }`}
        title="List View"
      >
        <ListBulletIcon className="h-5 w-5" />
      </button>
    </div>
  );

  const renderCardView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {customers.map((customer) => (
        <div
          key={customer._id}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border border-gray-200 dark:border-gray-700 cursor-pointer"
          onClick={() => handleViewDetails(customer)}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center flex-shrink-0">
                <UserGroupIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white truncate">
                  {customer.firstName && customer.lastName
                    ? `${customer.firstName} ${customer.lastName}`
                    : customer.customerId}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                  {customer.customerId}
                </p>
                {renderFamilyBadge(customer)}
              </div>
            </div>
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${getStatusBadge(
                customer.status,
              )}`}
            >
              {customer.status || "Active"}
            </span>
          </div>

          <div className="space-y-2 mb-4">
            {customer.email && (
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <EnvelopeIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                <span className="truncate">{customer.email}</span>
              </div>
            )}
            {(customer.phonePrefix || customer.phoneNumber) && (
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <PhoneIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                <span>
                  {customer.phonePrefix} {customer.phoneNumber}
                </span>
              </div>
            )}
            {customer.country && (
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <MapPinIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                <span>{customer.country}</span>
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetails(customer);
              }}
              className="w-full bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors text-sm"
            >
              View Details
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderListView = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Customer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {customers.map((customer) => (
              <tr
                key={customer._id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                onClick={() => handleViewDetails(customer)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {customer.firstName && customer.lastName
                        ? `${customer.firstName} ${customer.lastName}`
                        : "N/A"}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {customer.customerId}
                    </div>
                    {renderFamilyBadge(customer)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 dark:text-white">
                    {customer.email}
                  </div>
                  {(customer.phonePrefix || customer.phoneNumber) && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {customer.phonePrefix} {customer.phoneNumber}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {customer.country || "N/A"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                      customer.status,
                    )}`}
                  >
                    {customer.status || "Active"}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetails(customer);
                    }}
                    className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300"
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Customers
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage customer accounts
          </p>
        </div>
        <button
          onClick={() => setIsExportModalOpen(true)}
          className="mt-3 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
          Export to Excel
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total Customers
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.total || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Active
            </div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {stats.active || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Inactive
            </div>
            <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
              {stats.inactive || 0}
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0 lg:space-x-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <SearchBar
              onSearch={setSearchQuery}
              initialValue={searchQuery}
              placeholder="Search customers..."
            />
          </div>

          {/* Filters and View Mode */}
          <div className="flex items-center space-x-3">
            {/* View Mode Toggle */}
            {renderViewModeButtons()}

            {/* Status Filter */}
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as FilterType)}
                className="rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
              >
                <option value="all">All Customers</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Customers Content */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      ) : customers.length > 0 ? (
        <div className="space-y-4">
          {viewMode === "card" ? renderCardView() : renderListView()}

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
          />
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center border border-gray-200 dark:border-gray-700">
          <div className="text-gray-400 dark:text-gray-500 mb-3">
            <UserGroupIcon className="h-12 w-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No customers found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {searchQuery || filter !== "all"
              ? "Try adjusting your search or filters"
              : "No customers registered yet"}
          </p>
        </div>
      )}

      {/* Export Modal */}
      <ExportCustomersModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        customers={customers}
        totalCustomers={totalItems}
        isLoadingAll={isLoadingAllCustomers}
        onLoadAllCustomers={loadAllCustomers}
      />

      {/* Customer Detail Modal */}
      {selectedCustomer && (
        <div
          className={`fixed inset-0 z-50 overflow-y-auto ${
            isDetailModalOpen ? "block" : "hidden"
          }`}
        >
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={handleCloseDetailModal}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
              {/* Header */}
              <div className="bg-primary-600 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <UserGroupIcon className="h-8 w-8 text-white" />
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      {selectedCustomer.firstName && selectedCustomer.lastName
                        ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}`
                        : "Customer Details"}
                    </h3>
                    <p className="text-sm text-primary-100">
                      {selectedCustomer.customerId}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseDetailModal}
                  className="text-white hover:text-gray-200 transition-colors"
                  aria-label="Close customer details"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="bg-white dark:bg-gray-800 px-6 py-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                <div className="space-y-6">
                  {/* Basic Information */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <UserGroupIcon className="h-5 w-5 mr-2" />
                      Basic Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Customer ID
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">
                          {selectedCustomer.customerId}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Status
                        </label>
                        <p className="mt-1">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                              selectedCustomer.status,
                            )}`}
                          >
                            {selectedCustomer.status || "Active"}
                          </span>
                        </p>
                      </div>
                      {selectedCustomer.firstName && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            First Name
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {selectedCustomer.firstName}
                          </p>
                        </div>
                      )}
                      {selectedCustomer.lastName && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Last Name
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {selectedCustomer.lastName}
                          </p>
                        </div>
                      )}
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide flex items-center justify-between">
                          <span>Email</span>
                          {!isEditingEmail && (
                            <button
                              onClick={handleStartEditEmail}
                              className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
                              title="Edit Email"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                        </label>
                        {isEditingEmail ? (
                          <div className="mt-1 flex items-center space-x-2">
                            <input
                              type="email"
                              value={editEmail}
                              onChange={(e) => setEditEmail(e.target.value)}
                              className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              autoFocus
                            />
                            <button
                              onClick={handleSaveEmail}
                              disabled={updateContactMutation.isPending}
                              className="text-green-600 hover:text-green-700 disabled:opacity-50"
                              title="Save"
                            >
                              <CheckIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={handleCancelEditEmail}
                              className="text-red-600 hover:text-red-700"
                              title="Cancel"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </div>
                        ) : (
                          <p className="mt-1 text-sm text-gray-900 dark:text-white break-all">
                            {selectedCustomer.email}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide flex items-center justify-between">
                          <span>Phone</span>
                          {!isEditingPhone && (
                            <button
                              onClick={handleStartEditPhone}
                              className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
                              title="Edit Phone"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                        </label>
                        {isEditingPhone ? (
                          <div className="mt-1 flex items-center space-x-2">
                            <input
                              type="text"
                              value={editPhonePrefix}
                              onChange={(e) =>
                                setEditPhonePrefix(e.target.value)
                              }
                              placeholder="+49"
                              className="w-16 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                            <input
                              type="text"
                              value={editPhoneNumber}
                              onChange={(e) =>
                                setEditPhoneNumber(e.target.value)
                              }
                              placeholder="Phone number"
                              className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              autoFocus
                            />
                            <button
                              onClick={handleSavePhone}
                              disabled={updateContactMutation.isPending}
                              className="text-green-600 hover:text-green-700 disabled:opacity-50"
                              title="Save"
                            >
                              <CheckIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={handleCancelEditPhone}
                              className="text-red-600 hover:text-red-700"
                              title="Cancel"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </div>
                        ) : (
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {selectedCustomer.phonePrefix ||
                            selectedCustomer.phoneNumber
                              ? `${selectedCustomer.phonePrefix || ""} ${
                                  selectedCustomer.phoneNumber || ""
                                }`
                              : "Not provided"}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide flex items-center justify-between">
                          <span>Country</span>
                          {!isEditingCountry && (
                            <button
                              onClick={() => {
                                setEditCountry(selectedCustomer.country || "");
                                setIsEditingCountry(true);
                              }}
                              className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                        </label>
                        {isEditingCountry ? (
                          <div className="mt-1 flex items-center space-x-2">
                            <input
                              type="text"
                              value={editCountry}
                              onChange={(e) => setEditCountry(e.target.value)}
                              className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              autoFocus
                            />
                            <button
                              onClick={async () => {
                                if (!selectedCustomer._id) return;
                                try {
                                  await updateCountryMutation.mutateAsync({
                                    customerId: selectedCustomer._id,
                                    country: editCountry,
                                  });
                                  setSelectedCustomer({
                                    ...selectedCustomer,
                                    country: editCountry,
                                  });
                                  setIsEditingCountry(false);
                                  toast.success("Country updated");
                                } catch {
                                  toast.error("Failed to update country");
                                }
                              }}
                              disabled={updateCountryMutation.isPending}
                              className="text-green-600 hover:text-green-700 disabled:opacity-50"
                            >
                              <CheckIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => setIsEditingCountry(false)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </div>
                        ) : (
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {selectedCustomer.country || "—"}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide flex items-center justify-between">
                          <span>ZIP Code</span>
                          {!isEditingZipCode && (
                            <button
                              onClick={() => {
                                setEditZipCode(selectedCustomer.zipCode || "");
                                setIsEditingZipCode(true);
                              }}
                              className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                        </label>
                        {isEditingZipCode ? (
                          <div className="mt-1 flex items-center space-x-2">
                            <input
                              type="text"
                              value={editZipCode}
                              onChange={(e) => setEditZipCode(e.target.value)}
                              className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              autoFocus
                            />
                            <button
                              onClick={async () => {
                                if (!selectedCustomer._id) return;
                                try {
                                  await updateZipCodeMutation.mutateAsync({
                                    customerId: selectedCustomer._id,
                                    zipCode: editZipCode,
                                  });
                                  setSelectedCustomer({
                                    ...selectedCustomer,
                                    zipCode: editZipCode,
                                  });
                                  setIsEditingZipCode(false);
                                  toast.success("ZIP code updated");
                                } catch {
                                  toast.error("Failed to update ZIP code");
                                }
                              }}
                              disabled={updateZipCodeMutation.isPending}
                              className="text-green-600 hover:text-green-700 disabled:opacity-50"
                            >
                              <CheckIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => setIsEditingZipCode(false)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </div>
                        ) : (
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {selectedCustomer.zipCode || "—"}
                          </p>
                        )}
                      </div>
                      {selectedCustomer.currency && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Currency
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {selectedCustomer.currency}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Address Information */}
                  {(selectedCustomer.street ||
                    selectedCustomer.houseNumber ||
                    selectedCustomer.city) && (
                    <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                        <MapPinIcon className="h-5 w-5 mr-2" />
                        Address Information
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedCustomer.street && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Street
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {selectedCustomer.street}
                            </p>
                          </div>
                        )}
                        {selectedCustomer.houseNumber && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              House Number
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {selectedCustomer.houseNumber}
                            </p>
                          </div>
                        )}
                        {selectedCustomer.addressAddition && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Address Addition
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {selectedCustomer.addressAddition}
                            </p>
                          </div>
                        )}
                        {selectedCustomer.city && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              City
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {selectedCustomer.city}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Bank Information */}
                  {(selectedCustomer.bankName ||
                    selectedCustomer.iban ||
                    selectedCustomer.bic) && (
                    <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Bank Information
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedCustomer.bankName && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Bank Name
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {selectedCustomer.bankName}
                            </p>
                          </div>
                        )}
                        {selectedCustomer.iban && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              IBAN
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono break-all">
                              {selectedCustomer.iban}
                            </p>
                          </div>
                        )}
                        {selectedCustomer.bic && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              BIC
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">
                              {selectedCustomer.bic}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Sponsor Information */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Sponsor Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Sponsor ID — inline edit */}
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide flex items-center justify-between">
                          <span>Sponsor ID</span>
                          {!isEditingSponsorId && (
                            <button
                              onClick={() => {
                                setEditSponsorId(
                                  selectedCustomer.sponsorId || "",
                                );
                                setIsEditingSponsorId(true);
                              }}
                              className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                        </label>
                        {isEditingSponsorId ? (
                          <div className="mt-1 flex items-center space-x-2">
                            <input
                              type="text"
                              value={editSponsorId}
                              onChange={(e) => setEditSponsorId(e.target.value)}
                              className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
                              autoFocus
                              placeholder="e.g. DD728C... or SR123"
                            />
                            <button
                              onClick={async () => {
                                if (!selectedCustomer.customerId) return;
                                try {
                                  await updateCustomerSponsorMutation.mutateAsync(
                                    {
                                      customerId: selectedCustomer.customerId,
                                      sponsorId: editSponsorId,
                                      sponsorType:
                                        (selectedCustomer.sponsorType as
                                          | "dealer"
                                          | "sales_rep"
                                          | "office") || "office",
                                    },
                                  );
                                  setSelectedCustomer({
                                    ...selectedCustomer,
                                    sponsorId: editSponsorId,
                                  });
                                  setIsEditingSponsorId(false);
                                  toast.success("Sponsor ID updated");
                                } catch {
                                  toast.error("Failed to update Sponsor ID");
                                }
                              }}
                              disabled={updateCustomerSponsorMutation.isPending}
                              className="text-green-600 hover:text-green-700 disabled:opacity-50"
                            >
                              <CheckIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => setIsEditingSponsorId(false)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </div>
                        ) : (
                          <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">
                            {selectedCustomer.sponsorId || "—"}
                          </p>
                        )}
                      </div>

                      {/* Sponsor Type — inline edit (dropdown) */}
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide flex items-center justify-between">
                          <span>Sponsor Type</span>
                          {!isEditingSponsorType && (
                            <button
                              onClick={() => {
                                setEditSponsorType(
                                  (selectedCustomer.sponsorType as
                                    | "dealer"
                                    | "sales_rep"
                                    | "office") || "office",
                                );
                                setIsEditingSponsorType(true);
                              }}
                              className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                        </label>
                        {isEditingSponsorType ? (
                          <div className="mt-1 flex items-center space-x-2">
                            <select
                              value={editSponsorType}
                              onChange={(e) =>
                                setEditSponsorType(
                                  e.target.value as
                                    | "dealer"
                                    | "sales_rep"
                                    | "office",
                                )
                              }
                              className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                              <option value="dealer">Dealer</option>
                              <option value="sales_rep">Sales Rep</option>
                              <option value="office">Office</option>
                            </select>
                            <button
                              onClick={async () => {
                                if (!selectedCustomer.customerId) return;
                                try {
                                  await updateCustomerSponsorMutation.mutateAsync(
                                    {
                                      customerId: selectedCustomer.customerId,
                                      sponsorId:
                                        selectedCustomer.sponsorId || "",
                                      sponsorType: editSponsorType,
                                    },
                                  );
                                  setSelectedCustomer({
                                    ...selectedCustomer,
                                    sponsorType: editSponsorType,
                                  });
                                  setIsEditingSponsorType(false);
                                  toast.success("Sponsor type updated");
                                } catch {
                                  toast.error("Failed to update sponsor type");
                                }
                              }}
                              disabled={updateCustomerSponsorMutation.isPending}
                              className="text-green-600 hover:text-green-700 disabled:opacity-50"
                            >
                              <CheckIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => setIsEditingSponsorType(false)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </div>
                        ) : (
                          <p className="mt-1 text-sm text-gray-900 dark:text-white capitalize">
                            {selectedCustomer.sponsorType
                              ? selectedCustomer.sponsorType.replace("_", " ")
                              : "—"}
                          </p>
                        )}
                      </div>

                      {selectedCustomer.customerIdSalesBackoffice && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Sales Backoffice ID
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">
                            {selectedCustomer.customerIdSalesBackoffice}
                          </p>
                        </div>
                      )}
                      {selectedCustomer.referralCustomerId && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Invited By (Customer ID)
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">
                            {selectedCustomer.referralCustomerId}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Family Account */}
                  {isDetailModalOpen && selectedCustomer.customerId && (
                    <FamilyAccountSection
                      key={selectedCustomer.customerId}
                      customerId={selectedCustomer.customerId}
                      familyBadge={selectedCustomer.familyBadge}
                      scrollIntoViewOnMount={focusFamilySection}
                    />
                  )}

                  {/* Legal & Compliance */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Legal & Compliance
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedCustomer.privacyCheck !== undefined && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Privacy Policy Accepted
                          </label>
                          <p className="mt-1">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                selectedCustomer.privacyCheck
                                  ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
                                  : "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200"
                              }`}
                            >
                              {selectedCustomer.privacyCheck ? "Yes" : "No"}
                            </span>
                          </p>
                        </div>
                      )}
                      {selectedCustomer.privacyAcceptedAt && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Privacy Accepted At
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {new Date(
                              selectedCustomer.privacyAcceptedAt,
                            ).toLocaleString()}
                          </p>
                        </div>
                      )}
                      {selectedCustomer.communicationConsent !== undefined && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Communication Consent
                          </label>
                          <p className="mt-1">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                selectedCustomer.communicationConsent
                                  ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
                                  : "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300"
                              }`}
                            >
                              {selectedCustomer.communicationConsent
                                ? "Yes"
                                : "No"}
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Admin Password Change */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <LockClosedIcon className="h-5 w-5 mr-2" />
                      Change Password
                    </h4>
                    {!isChangingPassword ? (
                      <button
                        onClick={() => setIsChangingPassword(true)}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                      >
                        <KeyIcon className="h-4 w-4 mr-2" />
                        Set New Password
                      </button>
                    ) : (
                      <div className="space-y-3 max-w-md">
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            New Password
                          </label>
                          <div className="mt-1 relative">
                            <input
                              type={showNewPassword ? "text" : "password"}
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              placeholder="Enter new password (min 6 characters)"
                              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white pr-10"
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setShowNewPassword(!showNewPassword)
                              }
                              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            >
                              {showNewPassword ? (
                                <EyeSlashIcon className="h-4 w-4" />
                              ) : (
                                <EyeIcon className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Confirm Password
                          </label>
                          <div className="mt-1 relative">
                            <input
                              type={showConfirmPassword ? "text" : "password"}
                              value={confirmPassword}
                              onChange={(e) =>
                                setConfirmPassword(e.target.value)
                              }
                              placeholder="Confirm new password"
                              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white pr-10"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            >
                              {showConfirmPassword ? (
                                <EyeSlashIcon className="h-4 w-4" />
                              ) : (
                                <EyeIcon className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>
                        {newPassword &&
                          confirmPassword &&
                          newPassword !== confirmPassword && (
                            <p className="text-xs text-red-500">
                              Passwords do not match
                            </p>
                          )}
                        {newPassword &&
                          newPassword.length > 0 &&
                          newPassword.length < 6 && (
                            <p className="text-xs text-red-500">
                              Password must be at least 6 characters
                            </p>
                          )}
                        <div className="flex items-center space-x-3 pt-1">
                          <button
                            onClick={handleChangePassword}
                            disabled={
                              changePasswordMutation.isPending ||
                              !newPassword ||
                              !confirmPassword ||
                              newPassword !== confirmPassword ||
                              newPassword.length < 6
                            }
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {changePasswordMutation.isPending
                              ? "Updating..."
                              : "Update Password"}
                          </button>
                          <button
                            onClick={() => {
                              setIsChangingPassword(false);
                              setNewPassword("");
                              setConfirmPassword("");
                              setShowNewPassword(false);
                              setShowConfirmPassword(false);
                            }}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Timestamps */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Timestamps
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedCustomer.createdAt && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Created At
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {new Date(
                              selectedCustomer.createdAt,
                            ).toLocaleString()}
                          </p>
                        </div>
                      )}
                      {selectedCustomer.updatedAt && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Updated At
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {new Date(
                              selectedCustomer.updatedAt,
                            ).toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;
